import type {
  EventTrade,
  JobSubtype,
  JobTypeSubtype,
  SubtypeTradeList,
  TimesheetDepartment,
} from '@kalos/kalos-rpc';

/**
 * The four trades (ticket 86bbnhjch: "Trade offers HVAC, Plumbing and Electrical", plus Refrigeration
 * for the commercial side). Trade ids share the department id space (migration 628), and `event_trade`
 * carries one row per department, so Trade pickers whitelist these instead of listing every department.
 */
export const TRADE_IDS = [20, 34, 15, 17] as const;

export const isTradeId = (id: number) => (TRADE_IDS as readonly number[]).includes(id);

export const RESIDENTIAL_MEP_DEPARTMENT_ID = 20;
/** Trades a Residential-MEP job can file under (ticket 86bc1cauv); the only multi-trade department. Same hardcoding as firstCallsTradeTabs.ts. */
export const MEP_TRADE_IDS = [20, 34, 15] as const;
export const isMepDepartment = (id: number | string) =>
  Number(id) === RESIDENTIAL_MEP_DEPARTMENT_ID;
/**
 * `job_subtype` rows are legacy data, never seeded by core; id 10 is Refrigeration on prod and dev.
 * Hidden under Residential-MEP (ticket 86bbnhjch: "Refrigeration is not selectable when Department is
 * Residential MEP").
 */
export const REFRIGERATION_SUBTYPE_ID = 10;

/** Shown under Sub Type when the department requires one. */
export const SUBTYPE_REQUIRED_MESSAGE = 'Sub Type is required for this department';

/** Stored trade, else the department's default (its own id); matches dispatch_calls. */
export const effectiveEventTradeId = (
  event: { eventTradeId: number; departmentId: number },
  departmentDefaultTradeId?: number,
) => event.eventTradeId || departmentDefaultTradeId || event.departmentId;

/** Resolve answer when known, else Residential-MEP. */
export const isSubtypeRequired = (departmentId: number, requiresSubtype?: boolean) =>
  requiresSubtype ?? departmentId === RESIDENTIAL_MEP_DEPARTMENT_ID;

/** Trade name, else the department description, else the id. */
export const tradeLabel = (
  id: number,
  trades: Pick<EventTrade, 'id' | 'name'>[],
  departments: Pick<TimesheetDepartment, 'id' | 'description'>[],
): string => {
  if (id === 0) return 'No Trade';
  const trade = trades.find((t) => t.id === id);
  if (trade?.name) return trade.name;
  const department = departments.find((d) => d.id === id);
  return department?.description || String(id);
};

export type SubtypeOption = { label: string; value: number };

/**
 * Sub Type options for a job form. `links` narrows to the job type; omit it to list every subtype.
 * Required departments get no placeholder; Residential-MEP hides Refrigeration.
 */
export const buildSubtypeOptions = ({
  departmentId,
  jobTypeId,
  subtypes,
  links,
  placeholderLabel,
  requiresSubtype,
  selectedId = 0,
}: {
  departmentId: number;
  /** Used with `links`. */
  jobTypeId?: number;
  subtypes: JobSubtype[];
  links?: JobTypeSubtype[];
  placeholderLabel: string;
  requiresSubtype?: boolean;
  /** Kept in the list even when hidden, so an existing value still renders. */
  selectedId?: number;
}): SubtypeOption[] => {
  const required = isSubtypeRequired(departmentId, requiresSubtype);
  const hideRefrigeration = departmentId === RESIDENTIAL_MEP_DEPARTMENT_ID;
  const ids = links
    ? links.filter((l) => l.jobTypeId === jobTypeId).map((l) => l.jobSubtypeId)
    : subtypes.map((s) => s.id);
  const options = ids
    .filter((id) => id > 0)
    .filter((id) => !hideRefrigeration || id !== REFRIGERATION_SUBTYPE_ID || id === selectedId)
    .map((id) => ({
      value: id,
      label: subtypes.find((s) => s.id === id)?.name.trim() || `Subtype ${id}`,
    }));
  if (!options.length) return [];
  return required ? options : [{ label: placeholderLabel, value: 0 }, ...options];
};

/** Sub Type dropdown group headers, in display order (ticket 86bcndkpm). Same hardcoding as firstCallsTradeTabs.ts. */
const SUBTYPE_GROUP_LABELS: [tradeId: number, label: string][] = [
  [20, 'HVAC'],
  [34, 'Plumbing'],
  [15, 'Electrical'],
];
export const SUBTYPE_OTHER_GROUP_LABEL = 'Other';

export type SubtypeOptionGroup = {
  key: string;
  /** No label: render the options without a header (placeholder, or an ungrouped list). */
  label?: string;
  options: SubtypeOption[];
};

/**
 * Groups Sub Type options under the trade each one routes to (GetSubtypeTrades), HVAC, Plumbing,
 * Electrical, then Other for anything else. Only departments whose trade is decided by the Sub Type
 * (`groupsByTrade`) are grouped; otherwise, or with no answer, it is one unlabelled group.
 * The placeholder (value 0) stays first and ungrouped. Options are only regrouped, never changed.
 */
export const groupSubtypeOptions = (
  options: SubtypeOption[],
  subtypeTrades?: SubtypeTradeList | null,
): SubtypeOptionGroup[] => {
  if (!options.length) return [];
  if (!subtypeTrades?.groupsByTrade) return [{ key: 'all', options }];
  const tradeOf = new Map(subtypeTrades.results.map((r) => [r.jobSubtypeId, r.eventTradeId]));
  const placeholder = options.filter((o) => o.value === 0);
  const rest = options.filter((o) => o.value !== 0);
  const known = new Set(SUBTYPE_GROUP_LABELS.map(([id]) => id));
  const groups: SubtypeOptionGroup[] = [
    ...(placeholder.length ? [{ key: 'placeholder', options: placeholder }] : []),
    ...SUBTYPE_GROUP_LABELS.map(([tradeId, label]) => ({
      key: `trade-${tradeId}`,
      label,
      options: rest.filter((o) => tradeOf.get(o.value) === tradeId),
    })),
    {
      key: 'other',
      label: SUBTYPE_OTHER_GROUP_LABEL,
      options: rest.filter((o) => !known.has(tradeOf.get(o.value) ?? 0)),
    },
  ];
  return groups.filter((g) => g.options.length > 0);
};

/** Each trade's catch-all Sub Type, pre-selected when booking from that trade's chip. */
export const TRADE_CATCH_ALL_SUBTYPE_ID: Record<number, number> = { 34: 12, 15: 1 };

/** The trade a new or edited job is being booked under. */
export type BookingTrade = {
  tradeId: number;
  /** The department's default trade. */
  deptTradeId: number;
  multiTrade: boolean;
};
